import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Label,
  Message,
  Modal,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../helpers';

const emptyForm = {
  id: 0,
  private_model: '',
  public_model: '',
  public_channel_id: 0,
  threshold: 10,
  enabled: false,
};

const ModelOverflowRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [privateModelAdded, setPrivateModelAdded] = useState([]);
  // 阈值的共享范围说明（per_node / global）—— **由后端按当前部署形态下发**，
  // 前端不写死文案：v1.17 未配 Redis = 单节点，v1.18 配 Redis 后 = 全局。
  const [thresholdSemantics, setThresholdSemantics] = useState(null);

  const channelMap = useMemo(() => {
    return new Map(channels.map((channel) => [channel.id, channel]));
  }, [channels]);

  const privateModelOptions = useMemo(() => {
    const models = new Set(privateModelAdded);
    channels.forEach((channel) => {
      channel.models?.forEach((model) => models.add(model));
    });
    return Array.from(models)
      .sort()
      .map((model) => ({ key: model, text: model, value: model }));
  }, [channels, privateModelAdded]);

  const selectedChannel = channelMap.get(form.public_channel_id);
  const publicModelOptions = (selectedChannel?.models || []).map((model) => ({
    key: model,
    text: model,
    value: model,
  }));

  const loadData = async () => {
    setLoading(true);
    try {
      const [routeRes, optionRes] = await Promise.all([
        API.get('/api/overflow_route/'),
        API.get('/api/overflow_route/options'),
      ]);
      if (!routeRes.data.success) {
        showError(routeRes.data.message || '加载路由失败');
        return;
      }
      if (!optionRes.data.success) {
        showError(optionRes.data.message || '加载渠道选项失败');
        return;
      }
      setRoutes(routeRes.data.data || []);
      setChannels(optionRes.data.data?.channels || []);
      setThresholdSemantics(optionRes.data.data?.threshold_semantics || null);
    } catch (error) {
      showError(error.message || '加载路由设置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setPrivateModelAdded([]);
    setModalOpen(true);
  };

  const openEdit = (route) => {
    setForm({
      id: route.id,
      private_model: route.private_model,
      public_model: route.public_model,
      public_channel_id: route.public_channel_id,
      threshold: route.threshold,
      enabled: route.enabled,
    });
    setPrivateModelAdded([route.private_model]);
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.private_model) {
      showError('请选择或填写私有模型');
      return;
    }
    if (!form.public_channel_id) {
      showError('请选择公网渠道');
      return;
    }
    if (!form.public_model) {
      showError('请选择公网模型');
      return;
    }
    const threshold = Number(form.threshold);
    if (!Number.isInteger(threshold) || threshold <= 0) {
      showError('并发阈值必须是大于 0 的整数');
      return;
    }

    const payload = {
      id: form.id,
      private_model: String(form.private_model).trim(),
      public_model: String(form.public_model).trim(),
      public_channel_id: Number(form.public_channel_id),
      threshold,
      enabled: form.enabled,
    };

    setSaving(true);
    try {
      const res = form.id
        ? await API.put('/api/overflow_route/', payload)
        : await API.post('/api/overflow_route/', payload);
      const { success, message } = res.data;
      if (!success) {
        showError(message || '保存失败');
        return;
      }
      showSuccess(form.id ? '路由已更新' : '路由已创建');
      setModalOpen(false);
      await loadData();
    } catch (error) {
      showError(error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (route, enabled) => {
    try {
      const res = await API.post(`/api/overflow_route/${route.id}/status`, {
        enabled,
      });
      const { success, message } = res.data;
      if (!success) {
        showError(message || '操作失败');
        return;
      }
      showSuccess(enabled ? '已启用' : '已停用');
      await loadData();
    } catch (error) {
      showError(error.message || '操作失败');
    }
  };

  const removeRoute = async (route) => {
    if (!window.confirm(`确定删除 ${route.private_model} 的溢出路由吗？`)) {
      return;
    }
    try {
      const res = await API.delete(`/api/overflow_route/${route.id}`);
      const { success, message } = res.data;
      if (!success) {
        showError(message || '删除失败');
        return;
      }
      showSuccess('路由已删除');
      await loadData();
    } catch (error) {
      showError(error.message || '删除失败');
    }
  };

  const renderChannel = (channelId) => {
    const channel = channelMap.get(channelId);
    if (!channel) {
      return <Label basic color='grey'>#{channelId}</Label>;
    }
    return (
      <Label basic color={channel.status === 1 ? 'blue' : 'grey'}>
        #{channel.id} {channel.name}
      </Label>
    );
  };

  return (
    <>
      <Header as='h4'>模型溢出路由</Header>
      <Message info>
        当私有模型的并发数达到阈值时，安全请求会转发到绑定的公网模型；命中敏感规则的请求仍留在私有模型。
        溢出后实际发往上游的模型名即「公网模型」，无需再在公网渠道配置模型重定向；公网模型可与私有模型同名（此时只换渠道）。公网渠道不可用（禁用/删除/不再支持该模型）时，安全请求会自动降级回私有渠道，响应头 X-Model-Route-Reason 为 overflow_fallback_private。
      </Message>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '12px',
        }}
      >
        <Button primary icon='plus' content='新增路由' onClick={openCreate} />
      </div>
      <Table basic='very' compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>私有模型</Table.HeaderCell>
            <Table.HeaderCell>公网模型</Table.HeaderCell>
            <Table.HeaderCell>公网渠道</Table.HeaderCell>
            <Table.HeaderCell>并发阈值</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>更新时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loading ? (
            <Table.Row>
              <Table.Cell colSpan='7'>加载中...</Table.Cell>
            </Table.Row>
          ) : routes.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan='7'>暂无模型溢出路由</Table.Cell>
            </Table.Row>
          ) : (
            routes.map((route) => (
              <Table.Row key={route.id}>
                <Table.Cell>{route.private_model}</Table.Cell>
                <Table.Cell>{route.public_model}</Table.Cell>
                <Table.Cell>{renderChannel(route.public_channel_id)}</Table.Cell>
                <Table.Cell>{route.threshold}</Table.Cell>
                <Table.Cell>
                  <Label basic color={route.enabled ? 'green' : 'grey'}>
                    {route.enabled ? '启用' : '停用'}
                  </Label>
                </Table.Cell>
                <Table.Cell>{timestamp2string(route.updated_time)}</Table.Cell>
                <Table.Cell>
                  <Button size='small' onClick={() => openEdit(route)}>
                    编辑
                  </Button>
                  <Button
                    size='small'
                    positive={route.enabled}
                    onClick={() => updateStatus(route, !route.enabled)}
                  >
                    {route.enabled ? '停用' : '启用'}
                  </Button>
                  <Button size='small' negative onClick={() => removeRoute(route)}>
                    删除
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size='small'>
        <Modal.Header>{form.id ? '编辑溢出路由' : '新增溢出路由'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Dropdown
                fluid
                search
                selection
                allowAdditions
                required
                label='私有模型'
                placeholder='选择或输入私有模型名'
                value={form.private_model}
                options={privateModelOptions}
                onChange={(_, { value }) =>
                  setForm((prev) => ({ ...prev, private_model: value }))
                }
                onAddItem={(_, { value }) =>
                  setPrivateModelAdded((prev) => [...prev, value])
                }
              />
              <Form.Input
                fluid
                required
                label='并发阈值'
                type='number'
                min='1'
                value={form.threshold}
                onChange={(_, { value }) =>
                  setForm((prev) => ({ ...prev, threshold: value }))
                }
              />
              {thresholdSemantics?.note ? (
                <div
                  style={{
                    fontSize: '0.82em',
                    lineHeight: '1.4',
                    color: thresholdSemantics.scope === 'global' ? '#21ba45' : '#b58105',
                    marginTop: '-0.6em',
                    marginBottom: '0.4em',
                  }}
                >
                  {thresholdSemantics.note}
                </div>
              ) : null}
              {thresholdSemantics?.note_sensitive ? (
                <div
                  style={{
                    fontSize: '0.82em',
                    lineHeight: '1.4',
                    color: '#9f3a38',
                    marginTop: '-0.4em',
                    marginBottom: '0.6em',
                  }}
                >
                  {thresholdSemantics.note_sensitive}
                </div>
              ) : null}
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Dropdown
                fluid
                search
                selection
                required
                label='公网渠道'
                placeholder='选择公网渠道'
                value={form.public_channel_id || ''}
                options={channels.map((channel) => ({
                  key: channel.id,
                  text: `#${channel.id} ${channel.name}${
                    channel.status === 1 ? '' : '（停用）'
                  }`,
                  value: channel.id,
                }))}
                onChange={(_, { value }) =>
                  setForm((prev) => ({
                    ...prev,
                    public_channel_id: value,
                    public_model: '',
                  }))
                }
              />
              <Form.Dropdown
                fluid
                search
                selection
                required
                disabled={!selectedChannel}
                label='公网模型'
                placeholder={selectedChannel ? '选择公网模型' : '请先选择渠道'}
                value={form.public_model}
                options={publicModelOptions}
                onChange={(_, { value }) =>
                  setForm((prev) => ({ ...prev, public_model: value }))
                }
              />
            </Form.Group>
            <Form.Field>
              <Checkbox
                toggle
                label='启用路由'
                checked={form.enabled}
                onChange={(_, { checked }) =>
                  setForm((prev) => ({ ...prev, enabled: checked }))
                }
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>取消</Button>
          <Button primary loading={saving} onClick={submit}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ModelOverflowRoutes;
